const LIFESPAN = 540; // 9s at 60fps

/**
 * Space XP crystal — cyan/emerald energy shard.
 * Collect for XP (and ability-threshold progress).
 */
export class Collectible {
    constructor(x, y) {
        this.reset(x, y);
    }

    reset(x, y) {
        this.x = x;
        this.y = y;
        this.radius = 9;
        this.collected = false;
        this.vx = (Math.random() - 0.5) * 5;
        this.vy = (Math.random() - 0.5) * 5;
        this.friction = 0.92;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.age = 0;
        this.lifespan = LIFESPAN;
        this.spin = Math.random() * Math.PI * 2;
        this.spinSpeed = 0.05 + Math.random() * 0.04;
        this.bob = Math.random() * Math.PI * 2;
        // XP granted on pickup
        this.xpValue = 1;
        // Visual: tall prism vs diamond
        this.style = Math.random() < 0.5 ? 'prism' : 'diamond';
        // Hue family: cyan / emerald / violet-ish XP
        const palettes = [
            { core: '#e8ffff', mid: '#33ffe0', deep: '#00a88a', glow0: 'rgba(0,255,200,0.45)', glow1: 'rgba(0,255,200,0.1)' },
            { core: '#f0ffe8', mid: '#66ff88', deep: '#00aa44', glow0: 'rgba(80,255,140,0.45)', glow1: 'rgba(80,255,140,0.1)' },
            { core: '#f0e8ff', mid: '#b388ff', deep: '#6644cc', glow0: 'rgba(160,120,255,0.4)', glow1: 'rgba(160,120,255,0.1)' },
        ];
        this.palette = palettes[Math.floor(Math.random() * palettes.length)];
    }

    /**
     * Returns: 'collected' | 'expired' | null
     */
    update(player) {
        this.age++;
        this.pulsePhase += 0.12;
        this.spin += this.spinSpeed;
        this.bob += 0.08;

        this.vx *= this.friction;
        this.vy *= this.friction;
        this.x += this.vx;
        this.y += this.vy;

        const dx = player.x - this.x;
        const dy = player.y - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;

        // Stronger base attract than coins — XP shards home in
        const magnetR = Math.max(95, (player.magnetRange || 0) + 90);
        if (dist < magnetR) {
            const pull = 0.3 + (1 - dist / magnetR) * 0.7;
            this.vx += (dx / dist) * pull;
            this.vy += (dy / dist) * pull;
        }

        if (dist < player.size * 0.65 + this.radius + 7) {
            this.collected = true;
            return 'collected';
        }

        if (this.age >= this.lifespan) return 'expired';
        return null;
    }

    draw(ctx) {
        const progress = this.age / this.lifespan;
        let alpha = progress > 0.78 ? 1 - (progress - 0.78) / 0.22 : 1;
        if (progress > 0.78 && Math.sin(this.age * 0.55) < 0) alpha *= 0.5;

        const pulse = 1 + Math.sin(this.pulsePhase) * 0.08;
        const bobY = Math.sin(this.bob) * 2.5;
        const p = this.palette;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y + bobY);
        ctx.rotate(this.spin * 0.35);
        ctx.scale(pulse, pulse);

        // Outer energy bloom
        const bloom = ctx.createRadialGradient(0, 0, 0, 0, 0, this.radius * 2.6);
        bloom.addColorStop(0, p.glow0);
        bloom.addColorStop(0.55, p.glow1);
        bloom.addColorStop(1, 'transparent');
        ctx.fillStyle = bloom;
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * 2.6, 0, Math.PI * 2);
        ctx.fill();

        if (this.style === 'prism') {
            this._drawPrism(ctx, p);
        } else {
            this._drawDiamond(ctx, p);
        }

        // Orbiting sparkle
        const a = this.pulsePhase * 1.4;
        const ox = Math.cos(a) * this.radius * 1.15;
        const oy = Math.sin(a) * this.radius * 0.7;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.beginPath();
        ctx.arc(ox, oy, 1.6, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    }

    _drawPrism(ctx, p) {
        const s = this.radius;
        // Tall hexagonal crystal
        const body = ctx.createLinearGradient(-s, -s, s, s);
        body.addColorStop(0, p.core);
        body.addColorStop(0.35, p.mid);
        body.addColorStop(1, p.deep);
        ctx.fillStyle = body;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.35);
        ctx.lineTo(s * 0.55, -s * 0.55);
        ctx.lineTo(s * 0.55, s * 0.55);
        ctx.lineTo(0, s * 1.15);
        ctx.lineTo(-s * 0.55, s * 0.55);
        ctx.lineTo(-s * 0.55, -s * 0.55);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
        ctx.lineWidth = 1.3;
        ctx.stroke();

        // Center ridge highlight
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.1);
        ctx.lineTo(0, s * 0.95);
        ctx.stroke();

        // Left dark facet
        ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.35);
        ctx.lineTo(-s * 0.55, -s * 0.55);
        ctx.lineTo(-s * 0.55, s * 0.55);
        ctx.lineTo(0, s * 1.15);
        ctx.closePath();
        ctx.fill();

        // Top glint
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.2);
        ctx.lineTo(s * 0.22, -s * 0.7);
        ctx.lineTo(-s * 0.08, -s * 0.55);
        ctx.closePath();
        ctx.fill();
    }

    _drawDiamond(ctx, p) {
        const s = this.radius;
        const body = ctx.createLinearGradient(0, -s, 0, s);
        body.addColorStop(0, p.core);
        body.addColorStop(0.4, p.mid);
        body.addColorStop(1, p.deep);
        ctx.fillStyle = body;
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.2);
        ctx.lineTo(s * 0.85, 0);
        ctx.lineTo(0, s * 1.2);
        ctx.lineTo(-s * 0.85, 0);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 1.3;
        ctx.stroke();

        // Crown facets
        ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
        ctx.beginPath();
        ctx.moveTo(0, -s * 1.2);
        ctx.lineTo(s * 0.4, -s * 0.15);
        ctx.lineTo(0, s * 0.1);
        ctx.lineTo(-s * 0.4, -s * 0.15);
        ctx.closePath();
        ctx.fill();

        // Bottom shadow facet
        ctx.fillStyle = 'rgba(0, 40, 30, 0.28)';
        ctx.beginPath();
        ctx.moveTo(0, s * 0.1);
        ctx.lineTo(s * 0.85, 0);
        ctx.lineTo(0, s * 1.2);
        ctx.closePath();
        ctx.fill();
    }
}
